import React from 'react';
import styled from 'styled-components';
import {
  AviveCheckIcon,
  AviveHeartIcon,
  AviveLoadingIcon,
  XCloseIcon,
} from '@rsos/assets/icons';
import AviveWarningIcon from '@rsos/assets/icons/src/AviveWarningIcon';
import { Box as BaseBox, Text } from '@rsos/components/capstone/base';
import { Button, StyledAviveStatus, StyledIcon } from './AviveStatus.styles';
import AED_STATUS, {
  AVIVE2_STATUS_DEVICE_ACTIVATED,
  AVIVE_STATUS_REQUEST,
  AVIVE_STATUS_SEARCHING,
  AVIVE2_STATUS_AED_CONCLUDED,
  AVIVE2_STATUS_AED_CANCELED,
  AVIVE2_STATUS_CANCELING,
  AVIVE2_STATUS_NOTE_SENT,
  AVIVE2_STATUS_UNABLE_TO_SEND_NOTE,
  AVIVE2_STATUS_CANCELLATION_FAILED,
  AVIVE_STATUS_FAILED_TO_CONTACT,
  AVIVE_STATUS_FAILURE,
  AVIVE2_STATUS_DUPLICATE_REQUEST_ERROR,
  AVIVE2_STATUS_EMERGENCY_TIMEOUT,
  AVIVE_STATUS_CONTACT_TIMEOUT,
} from './aviveStatusTypes';

const UnstyledButton = styled.button`
  outline: 0;
  padding: 0;
  margin: 0;
  background: transparent;
  border: 0;
  cursor: pointer;
`;

const CancelButton = styled(Button)`
  width: 100%;
  border: 1px solid white;
`;

const FakeLink = styled(UnstyledButton)`
  color: ${({ theme }) => theme.colors.background};
  text-decoration: underline;
`;

export const Spin = styled.div`
  @keyframes rotate {
    0% {
      transform: rotate(360deg);
    }
    100% {
      transform: rotate(0deg);
    }
  }
  & {
    animation: rotate 1s infinite linear;
  }
`;

const Box = styled(BaseBox)`
  display: grid;
  grid-template-columns: auto 1fr auto;
`;

const findStatusComponent = (
  status,
  // eslint-disable-next-line no-unused-vars
  { children, loading, activated, closable, onClose, onCancel, onCancelRetry },
) => {
  switch (status) {
    case AVIVE_STATUS_REQUEST: {
      return (
        <StyledAviveStatus status={AED_STATUS.PENDING}>
          <Box horizontal align="center">
            <StyledIcon title="Avive AED">
              <Spin>
                <AviveLoadingIcon />
              </Spin>
            </StyledIcon>
            <Text size="normal">{status}</Text>
          </Box>
        </StyledAviveStatus>
      );
    }

    case AVIVE_STATUS_SEARCHING: {
      return (
        <StyledAviveStatus status={AED_STATUS.PENDING}>
          <Box horizontal align="center">
            <StyledIcon title="Avive AED">
              <Spin>
                <AviveLoadingIcon />
              </Spin>
            </StyledIcon>
            <Text size="normal">{status}</Text>
          </Box>
          <BaseBox mt={2}>
            <CancelButton onClick={onCancel}>Cancel</CancelButton>
          </BaseBox>
        </StyledAviveStatus>
      );
    }

    case AVIVE2_STATUS_DEVICE_ACTIVATED: {
      return (
        <StyledAviveStatus status={AED_STATUS.PENDING}>
          <Box horizontal align="center">
            <StyledIcon title="Avive AED">
              <AviveHeartIcon />
            </StyledIcon>
            <Text size="normal">
              <strong>Device Activated</strong>, AED is in use.
            </Text>
          </Box>
          <BaseBox mt={2}>
            <CancelButton onClick={onCancel}>Cancel</CancelButton>
          </BaseBox>
        </StyledAviveStatus>
      );
    }

    case AVIVE2_STATUS_AED_CONCLUDED: {
      return (
        <StyledAviveStatus status={AED_STATUS.SUCCESS}>
          <Box horizontal align="center">
            <StyledIcon title="Avive AED">
              <AviveCheckIcon />
            </StyledIcon>
            <Text size="normal">
              <Text fontWeight="bold">Device Usage Concluded</Text>
              <Text>AED request has been resolved.</Text>
            </Text>
          </Box>
        </StyledAviveStatus>
      );
    }

    case AVIVE2_STATUS_CANCELING: {
      return (
        <StyledAviveStatus status={AED_STATUS.PENDING}>
          <Box horizontal align="center">
            <StyledIcon title="Avive AED">
              <Spin>
                <AviveLoadingIcon />
              </Spin>
            </StyledIcon>
            <Text size="normal">{status}</Text>
          </Box>
        </StyledAviveStatus>
      );
    }

    case AVIVE2_STATUS_AED_CANCELED: {
      return (
        <StyledAviveStatus status={AED_STATUS.SUCCESS}>
          <Box horizontal align="center">
            <StyledIcon title="Avive AED">
              <AviveCheckIcon />
            </StyledIcon>
            <Text size="normal">{status}</Text>
          </Box>
        </StyledAviveStatus>
      );
    }

    case AVIVE2_STATUS_NOTE_SENT: {
      return (
        <StyledAviveStatus status={AED_STATUS.SUCCESS}>
          <Box horizontal align="center">
            <StyledIcon title="Avive AED">
              <AviveCheckIcon />
            </StyledIcon>
            <Text size="normal">{status}</Text>
            <UnstyledButton
              onClick={event => {
                event.preventDefault();
                onClose();
              }}
            >
              <StyledIcon title="Avive AED">
                <XCloseIcon />
              </StyledIcon>
            </UnstyledButton>
          </Box>
        </StyledAviveStatus>
      );
    }

    case AVIVE2_STATUS_UNABLE_TO_SEND_NOTE: {
      return (
        <StyledAviveStatus status={AED_STATUS.FAILURE}>
          <Box horizontal align="center">
            <StyledIcon title="Avive AED">
              <AviveWarningIcon />
            </StyledIcon>
            <Text size="normal">{status}</Text>
            <UnstyledButton
              onClick={event => {
                event.preventDefault();
                onClose();
              }}
            >
              <StyledIcon title="Avive AED">
                <XCloseIcon />
              </StyledIcon>
            </UnstyledButton>
          </Box>
        </StyledAviveStatus>
      );
    }

    case AVIVE_STATUS_FAILURE:
    case AVIVE_STATUS_FAILED_TO_CONTACT:
    case AVIVE_STATUS_CONTACT_TIMEOUT:
    case AVIVE2_STATUS_CANCELLATION_FAILED: {
      return (
        <StyledAviveStatus status={AED_STATUS.FAILURE}>
          <Box horizontal align="center">
            <StyledIcon title="Avive AED">
              <AviveWarningIcon />
            </StyledIcon>
            <Text size="normal">{status}</Text>
          </Box>
        </StyledAviveStatus>
      );
    }
    case AVIVE2_STATUS_DUPLICATE_REQUEST_ERROR: {
      return (
        <StyledAviveStatus status={AED_STATUS.FAILURE}>
          <Box horizontal align="center">
            <StyledIcon title="Avive AED">
              <AviveWarningIcon />
            </StyledIcon>
            <BaseBox>
              <Text size="normal">{status}</Text>
              <Text size="small">
                <FakeLink onClick={onCancelRetry}>
                  Please try canceling again.
                </FakeLink>
              </Text>
            </BaseBox>
          </Box>
        </StyledAviveStatus>
      );
    }

    case AVIVE2_STATUS_EMERGENCY_TIMEOUT: {
      return (
        <StyledAviveStatus status={AED_STATUS.FAILURE}>
          <Box horizontal align="center">
            <StyledIcon title="Avive AED">
              <AviveWarningIcon />
            </StyledIcon>
            <BaseBox>
              <Text size="normal">Response timeout</Text>
            </BaseBox>
            <UnstyledButton
              onClick={event => {
                event.preventDefault();
                onClose();
              }}
            >
              <StyledIcon title="Avive AED">
                <XCloseIcon />
              </StyledIcon>
            </UnstyledButton>
          </Box>
        </StyledAviveStatus>
      );
    }

    default:
      return (
        <StyledAviveStatus status={AED_STATUS.PENDING}>
          <Box horizontal align="center">
            <StyledIcon title="Avive AED">
              {loading && (
                <Spin>
                  <AviveLoadingIcon />
                </Spin>
              )}
              {activated && <AviveHeartIcon />}
            </StyledIcon>
            <Text size="normal">{status}</Text>
          </Box>
        </StyledAviveStatus>
      );
  }
};

const AviveStandaloneStatus = ({
  status,
  children,
  activated = false,
  loading = false,
  closable = false,
  onClose = () => {},
  onCancel = () => {},
  onCancelRetry = () => {},
}) => {
  return findStatusComponent(status, {
    children,
    activated,
    loading,
    closable,
    onClose,
    onCancel,
    onCancelRetry,
  });
};

export default AviveStandaloneStatus;
